import { HelpOutline } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  CreateEvent,
  DistributionStatus,
  Event,
  EventWaitingType,
  PriorizationType
} from '@thro-types/events';

import { isBefore } from 'date-fns';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardControlButtons } from 'app/components';
import { PerformantTextField } from 'app/components/PerformantTextField/PerformantTextField';
import { translations } from 'locales/i18n';

import { isMaxParticipantFieldInvalid } from '../../helper';
import '../styles/EventModal.scss';

interface EventGroupOrganizationProps {
  previousEvent?: Event;
}

export const EventGroupOrganization = ({ previousEvent }: EventGroupOrganizationProps) => {
  const { t } = useTranslation();
  const ref = useRef<null | HTMLDivElement>(null);

  const { setFieldValue, values, validateField, setFieldError } = useFormikContext<
    CreateEvent & { distributionStatus: DistributionStatus }
  >();
  const [blockShowMore, setBlockShowMore] = useState(values.hasSameGroupSize);

  const [minParticipants, setMinParticipants] = useState(values.groups[0].minParticipants ?? 0);
  const [maxParticipants, setMaxParticipants] = useState<number | ''>(
    values.groups[0].maxParticipants ?? 0
  );
  const setParticipantsForAllGroups = async () => {
    const groups = values.groups.map((group, index) => {
      return {
        ...group,
        minParticipants: minParticipants,
        maxParticipants: maxParticipants
      };
    });
    setFieldValue('groups', groups);
  };

  const distributed =
    values.distributionStatus === DistributionStatus.DISTRIBUTED ||
    values.distributionStatus === DistributionStatus.DISTRIBUTING
      ? true
      : false;

  const handleMaxParticipantsChange = (value: number) => {
    setMaxParticipants(value);
    setFieldError('groups[0].maxParticipants', '');
    validateField(`groups[0].maxParticipants`);
  };

  return (
    <FieldArray name="groups">
      {({ remove, push }) => (
        <Grid container spacing={2}>
          <Grid item xs={12} className="event-modal__input">
            <FormControl required fullWidth disabled={distributed}>
              <FormLabel>{t(translations.pages.events.create.priorization())}</FormLabel>
              <Field as={Select} id="priorizationType" name="priorizationType" fullWidth>
                {Object.values(PriorizationType).map(priorizationType => (
                  <MenuItem
                    value={priorizationType}
                    key={priorizationType}
                    onBlur={e => {
                      if (values.waitingType === 'NONE') setFieldValue('waitingType', '');
                    }}
                  >
                    {t(translations.pages.events.create.priorizationType[priorizationType]())}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
            <FormControl required fullWidth disabled={distributed}>
              <Grid container>
                <FormLabel>{t(translations.pages.events.create.waitingList())}</FormLabel>
                {values.priorizationType === 'SEMESTER' && (
                  <Grid
                    item
                    container
                    xs
                    justifyContent="flex-end"
                    alignItems="self-end"
                    paddingBottom={'0.5em'}
                  >
                    <Tooltip title={t(translations.pages.events.tooltips.waitingList())}>
                      <HelpOutline fontSize="medium" />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
              <Field as={Select} id="waitingType" name="waitingType" fullWidth>
                {Object.values(EventWaitingType).map(eventWaitingType => (
                  <MenuItem
                    value={eventWaitingType}
                    disabled={
                      eventWaitingType === EventWaitingType.NONE &&
                      values.priorizationType === 'SEMESTER'
                    }
                    key={eventWaitingType}
                  >
                    <Tooltip
                      title={
                        eventWaitingType === EventWaitingType.EVENT
                          ? t(translations.pages.events.tooltips.oneWaitingListForEvent())
                          : ''
                      }
                    >
                      <div style={{ width: '100%' }}>
                        {t(translations.pages.events.create.waitingListTypes[eventWaitingType]())}
                      </div>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} className="event-modal__input">
              <FormLabel required>
                {t(translations.pages.events.create.groupManagement())}
              </FormLabel>
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={values.hasSameGroupSize}
                value={values.hasSameGroupSize}
                onChange={(_e, checked) => {
                  setFieldValue('hasSameGroupSize', checked);
                  setBlockShowMore(checked);
                  if (checked) {
                    setParticipantsForAllGroups();
                  }
                }}
                disabled={distributed}
              />
              <FormLabel sx={{ marginLeft: 1 }} color="primary">
                {t(translations.pages.events.create.sameGroupSize())}
              </FormLabel>
            </Stack>
          </Grid>
          {values.hasSameGroupSize && (
            <Grid item container spacing={2} className="event-group__appearField">
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  onBlur={e => {
                    setParticipantsForAllGroups();
                  }}
                  onChange={e => setMinParticipants(e.target.value)}
                  fullWidth
                  label={t(translations.pages.events.create.minParticipants())}
                  value={minParticipants}
                  required
                  disabled={distributed}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  type="number"
                  InputProps={{
                    inputProps: { min: minParticipants }
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleMaxParticipantsChange(e.target.valueAsNumber)
                  }
                  onBlur={e => {
                    setParticipantsForAllGroups();
                  }}
                  fullWidth
                  label={t(translations.pages.events.create.maxParticipants())}
                  value={maxParticipants}
                  required
                  disabled={distributed}
                  error={isMaxParticipantFieldInvalid(0, previousEvent, values.groups)}
                />
              </Grid>
            </Grid>
          )}
          <Grid item container spacing={2} xs={12} ref={ref}>
            {values.groups.map((group, index) => {
              return (
                <Grid item xs={12} className="event-group__appearAnimation" key={`${index}}`}>
                  <EventGroupCard
                    index={index}
                    remove={remove}
                    blockShowMore={blockShowMore}
                    disabled={distributed}
                    previousEvent={previousEvent}
                  />
                </Grid>
              );
            })}
          </Grid>
          <Grid item xs={12}>
            <Grid item>
              <Button
                onClick={() => {
                  push({
                    minParticipants: minParticipants,
                    maxParticipants: maxParticipants,
                    startDate: null,
                    endDate: null,
                    place: ''
                  });
                  setTimeout(() => {
                    //! Move to new created group
                    (ref.current?.lastChild as HTMLElement).scrollIntoView({
                      behavior: 'smooth'
                    });
                  }, 100);
                }}
                key="addNew"
                variant="contained"
                color="primary"
                style={{ color: 'white' }}
                type="button"
                disabled={distributed}
              >
                {t(translations.pages.events.create.addNewGroupButton())}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FieldArray>
  );
};

interface EventGroupCardProps {
  index: number;
  remove: <T>(index: number) => T | undefined;
  blockShowMore: boolean;
  disabled: boolean;
  previousEvent?: Event;
}

const EventGroupCard = (props: EventGroupCardProps) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(!props.blockShowMore);

  const { values, setFieldValue, validateField } = useFormikContext<CreateEvent>();

  useEffect(() => {
    if (props.blockShowMore === true) {
      setShowMore(!props.blockShowMore);
    }
  }, [props.blockShowMore]);

  const handleMaxParticipantsChange = (value: number) => {
    setFieldValue(`groups[${props.index}].maxParticipants`, value);
    validateField(`groups[${props.index}].maxParticipants`);
  };

  return (
    <Grid
      container
      item
      xs={12}
      component={Paper}
      elevation={3}
      spacing={1}
      style={{ paddingBottom: '2em' }}
      justifyContent="flex-start"
    >
      <Grid container justifyContent={'space-between'} alignItems="center">
        <Grid item className="event-group__eventGroupCard__name">
          <Typography component="h3">{`${t(translations.pages.events.create.groupCard.group())} ${
            props.index + 1
          }`}</Typography>
        </Grid>
        <Grid item>
          <CardControlButtons
            handleDelete={() => {
              props.remove(props.index);
            }}
            handleShowMore={() => {
              setShowMore(old => !old);
            }}
            blockShowMore={props.blockShowMore}
            showMore={showMore}
            blockDeleteButton={values.groups.length === 1 || props.disabled}
            index={props.index}
          />
        </Grid>
      </Grid>
      <Grid container item justifyContent="space-around" spacing={2}>
        <Grid item xs={11} container spacing={1}>
          <Grid item xs={12}>
            <PerformantTextField
              name={`groups[${props.index}].info`}
              fullWidth
              label={t(translations.pages.events.create.groupCard.info())}
              inputProps={{ maxLength: 300 }}
              disabled={props.disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <PerformantTextField
              name={`groups[${props.index}].place`}
              fullWidth
              label={t(translations.pages.events.create.groupCard.place())}
              disabled={props.disabled}
            />
          </Grid>
        </Grid>
        {showMore && (
          <Grid item xs={11} container spacing={1}>
            <Grid item xs={6}>
              <Field
                as={TextField}
                name={`groups[${props.index}].minParticipants`}
                type="number"
                InputProps={{
                  inputProps: { min: 0, max: values.groups[props.index].maxParticipants }
                }}
                fullWidth
                label={t(translations.pages.events.create.groupCard.minParticipants())}
                required
                disabled={props.disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <Tooltip
                title={
                  isBefore(new Date(values.registrationPeriodStart), new Date()) &&
                  props.previousEvent
                    ? t(translations.pages.events.tooltips.maxParticipantsTooltip())
                    : ''
                }
              >
                <TextField
                  value={values.groups[props.index].maxParticipants}
                  type="number"
                  InputProps={{
                    inputProps: {
                      min: values.groups[props.index].minParticipants
                    }
                  }}
                  onChange={e => {
                    handleMaxParticipantsChange(parseInt(e.target.value));
                  }}
                  fullWidth
                  label={t(translations.pages.events.create.groupCard.maxParticipants())}
                  required
                  disabled={props.disabled}
                  error={isMaxParticipantFieldInvalid(
                    props.index,
                    props.previousEvent,
                    values.groups
                  )}
                />
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
